import { TIMED_MATCH_DURATION_MS, zoneForHeight, type StackTowerRunResult } from '@ranked-waiting/stack-tower-core';
import { renderPageShell } from '../lib/page-shell';
import { mountStackTowerEngine, type StackTowerEngine } from '../lib/stack-tower-engine';
import { isStackTowerAudioMuted, setStackTowerAudioMuted } from '../lib/stack-tower-audio';
import { checkForNewAchievements } from '../lib/achievement-toast';
import { escapeHtml } from '../lib/format';
import { fetchMe } from '../lib/session';
import {
  fetchStackTowerLeaderboard,
  fetchStackTowerStats,
  submitStackTowerRun,
  type StackTowerLeaderboardRow,
  type StackTowerMode,
  type StackTowerStats,
} from '../lib/stack-tower';
import type { RouteHandler } from '../lib/router';

function formatDuration(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * One view backs both Solo modes — Endless (play until you miss) and 60
 * Second (spec §98: a fixed window, a miss soft-resets instead of ending the
 * run, owner's call to match head-to-head's own timed miss rule). They share
 * everything except the duration/miss-rule config, the HUD's timer, and
 * which leaderboard a run submits to — not enough divergence to justify two
 * separate files.
 */
function createStackTowerView(mode: StackTowerMode): RouteHandler {
  const timed = mode === '60s';
  return (container, navigate) => {
    const body = renderPageShell(container, navigate, 'play', {
      title: timed ? 'STACK TOWER: 60 SECOND' : 'STACK TOWER',
      subtitle: timed
        ? 'Tap to place. A miss resets your tower, not your score. How high can you climb in 60 seconds?'
        : 'Tap to place. Only the overlap survives. How high can you climb?',
      body: `<div class="stack-tower-stage" id="stack-tower-stage"></div>`,
    });

    const stage = body.querySelector<HTMLDivElement>('#stack-tower-stage')!;
    let engine: StackTowerEngine | undefined;
    let bestScore = 0;
    let timerIntervalId: number | undefined;
    // Wall-clock deadline for the HUD countdown only — shifted on resume to
    // match the engine's own internal (performance.now()-based) deadline
    // shift, so the two never visually disagree after a pause.
    let matchEndsAtMs = 0;
    // Rebound on every start() (a fresh pause/resume closure per run); a single
    // window-level listener for the component's whole lifetime just calls
    // whichever one is currently live, so there's nothing to add/remove per
    // restart — only at final teardown.
    let togglePauseFromKeyboard: (() => void) | null = null;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      togglePauseFromKeyboard?.();
    }
    window.addEventListener('keydown', onKeyDown);

    function start() {
      window.clearInterval(timerIntervalId);
      stage.innerHTML = `
      <div class="stack-tower-hud" id="stack-tower-hud" aria-live="polite">
        <span class="stack-tower-hud__score-row">
          <span class="stack-tower-hud__score" id="stack-tower-score">0</span>
          <span class="stack-tower-hud__multiplier" id="stack-tower-multiplier" hidden></span>
        </span>
        ${timed ? `<span class="stack-tower-hud__timer" id="stack-tower-timer">${formatClock(TIMED_MATCH_DURATION_MS)}</span>` : ''}
        <span class="stack-tower-hud__zone" id="stack-tower-zone">${zoneForHeight(0).label}</span>
        <span class="stack-tower-hud__streak" id="stack-tower-streak" hidden></span>
      </div>
      <button class="stack-tower-pause-button" id="stack-tower-pause-button" type="button" aria-label="Pause">❚❚</button>
      <button class="stack-tower-mute-button stack-tower-mute-button--paired" id="stack-tower-mute-button" type="button" aria-label="Mute sound">${isStackTowerAudioMuted() ? '🔇' : '🔊'}</button>
      <div class="stack-tower-mount" id="stack-tower-mount"></div>
      <p class="stack-tower-milestone" id="stack-tower-milestone" aria-hidden="true"></p>
      <div class="stack-tower-paused" id="stack-tower-paused" hidden>
        <p class="handoff__label">Paused</p>
        <button class="button button--primary" type="button" data-action="resume">Resume</button>
      </div>
    `;
      const mount = stage.querySelector<HTMLDivElement>('#stack-tower-mount')!;
      const scoreEl = stage.querySelector<HTMLSpanElement>('#stack-tower-score')!;
      const multiplierEl = stage.querySelector<HTMLSpanElement>('#stack-tower-multiplier')!;
      const zoneEl = stage.querySelector<HTMLSpanElement>('#stack-tower-zone')!;
      const streakEl = stage.querySelector<HTMLSpanElement>('#stack-tower-streak')!;
      const pauseButton = stage.querySelector<HTMLButtonElement>('#stack-tower-pause-button')!;
      const muteButton = stage.querySelector<HTMLButtonElement>('#stack-tower-mute-button')!;
      const pausedOverlay = stage.querySelector<HTMLDivElement>('#stack-tower-paused')!;
      const milestoneEl = stage.querySelector<HTMLParagraphElement>('#stack-tower-milestone')!;
      const timerEl = stage.querySelector<HTMLSpanElement>('#stack-tower-timer');
      const runId = crypto.randomUUID();

      if (timed) {
        matchEndsAtMs = Date.now() + TIMED_MATCH_DURATION_MS;
        timerIntervalId = window.setInterval(() => {
          if (timerEl) timerEl.textContent = formatClock(matchEndsAtMs - Date.now());
        }, 250);
      }

    // Visual Milestones (spec §51): a bigger, transient fade-in/out banner on
    // zone entry — distinct from the small persistent HUD zone label above,
    // which stays put. Never pauses gameplay; purely a CSS animation restart.
    let milestoneTimeoutId: number | undefined;
    function showMilestone(label: string) {
      window.clearTimeout(milestoneTimeoutId);
      milestoneEl.textContent = label;
      milestoneEl.classList.remove('is-showing');
      void milestoneEl.offsetWidth;
      milestoneEl.classList.add('is-showing');
      milestoneTimeoutId = window.setTimeout(() => milestoneEl.classList.remove('is-showing'), 2200);
    }

    let pausedAtWallClock = 0;
    function setPaused(next: boolean) {
      if (!engine) return;
      if (next) {
        engine.pause();
        pausedAtWallClock = Date.now();
      } else {
        engine.resume();
        // Shift the HUD's own wall-clock deadline forward by the same amount
        // the engine just shifted its internal one — otherwise the visible
        // countdown would disagree with how much time the engine actually
        // thinks is left.
        if (timed) matchEndsAtMs += Date.now() - pausedAtWallClock;
      }
      pausedOverlay.hidden = !next;
      pauseButton.hidden = next;
    }
    pauseButton.addEventListener('click', () => setPaused(true));
    pausedOverlay.querySelector<HTMLButtonElement>('[data-action="resume"]')?.addEventListener('click', () => setPaused(false));
    muteButton.addEventListener('click', () => {
      setStackTowerAudioMuted(!isStackTowerAudioMuted());
      muteButton.textContent = isStackTowerAudioMuted() ? '🔇' : '🔊';
    });
    togglePauseFromKeyboard = () => {
      if (!engine) return;
      setPaused(!engine.isPaused());
    };
    engine = mountStackTowerEngine(mount, {
      runId,
      durationMs: timed ? TIMED_MATCH_DURATION_MS : undefined,
      onScoreChanged: (score) => {
        scoreEl.textContent = String(score);
      },
      onMultiplierChanged: (multiplier) => {
        if (multiplier <= 1) {
          multiplierEl.hidden = true;
          return;
        }
        multiplierEl.textContent = `×${multiplier}`;
        multiplierEl.hidden = false;
        multiplierEl.classList.remove('is-pop');
        void multiplierEl.offsetWidth;
        multiplierEl.classList.add('is-pop');
      },
      onZoneChanged: (zone) => {
        zoneEl.textContent = zone.label;
        // Pulse on entry (neutralised under reduced motion by base.css; the
        // label itself stays visible so the current zone is always readable).
        zoneEl.classList.remove('is-pop');
        void zoneEl.offsetWidth;
        zoneEl.classList.add('is-pop');
        showMilestone(zone.label);
      },
      onPerfectStreakChanged: (streak) => {
        if (streak <= 0) {
          streakEl.hidden = true;
          return;
        }
        streakEl.textContent = streak === 1 ? 'PERFECT' : `PERFECT ×${streak}`;
        streakEl.hidden = false;
        // Restart the pop animation on every perfect (globally neutralised under
        // reduced motion by base.css, so no extra guard needed here).
        streakEl.classList.remove('is-pop');
        void streakEl.offsetWidth; // force reflow so the re-added class re-triggers the keyframes
        streakEl.classList.add('is-pop');
      },
      onGameOver: showGameOver,
    });
  }

  function summaryLine(result: StackTowerRunResult): string {
    return `
      ${result.perfectCount} perfect${result.perfectCount === 1 ? '' : 's'} ·
      best streak ${result.maxPerfectStreak} ·
      ${formatDuration(result.runDurationMs)}
      ${bestScore > result.score ? ` · best this session ${bestScore}` : ''}`;
  }

  function showGameOver(result: StackTowerRunResult) {
    window.clearInterval(timerIntervalId);
    engine?.destroy();
    engine = undefined;
    bestScore = Math.max(bestScore, result.score);

    stage.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Run over</p>
        <h2>${result.score}</h2>
        <p class="settings-hint">${summaryLine(result)}</p>
        <div class="stack-tower-submit" data-role="submit-area">
          <p class="settings-hint" data-role="status">Checking your leaderboard status…</p>
        </div>
        <div class="hero-actions">
          <button class="button button--primary" type="button" data-action="restart">Play again</button>
          <button class="button button--secondary" type="button" data-action="leaderboard">Leaderboard</button>
          <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
        </div>
      </div>
    `;
    stage.querySelector<HTMLButtonElement>('[data-action="restart"]')?.addEventListener('click', start);
    stage.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => navigate('play'));
    stage
      .querySelector<HTMLButtonElement>('[data-action="leaderboard"]')
      ?.addEventListener('click', () => void showLeaderboard(null));

    const submitArea = stage.querySelector<HTMLDivElement>('[data-role="submit-area"]')!;

    // Signed in → auto-submit under the real username; guest → offer a name form.
    void fetchMe()
      .catch(() => null)
      .then((me) => {
        if (me) {
          void autoSubmit(result, submitArea);
        } else {
          renderGuestForm(result, submitArea);
        }
      });
  }

  function payloadFor(result: StackTowerRunResult, displayName?: string) {
    return {
      runId: result.runId,
      displayName,
      score: result.score,
      maxPerfectStreak: result.maxPerfectStreak,
      perfectCount: result.perfectCount,
      finalWidthFraction: result.finalWidthFraction,
      zoneId: result.zoneId,
      runDurationMs: Math.round(result.runDurationMs),
      mode,
    };
  }

  async function autoSubmit(result: StackTowerRunResult, submitArea: HTMLDivElement) {
    const status = submitArea.querySelector<HTMLParagraphElement>('[data-role="status"]')!;
    status.textContent = 'Submitting your run…';
    try {
      const submitted = await submitStackTowerRun(payloadFor(result));
      checkForNewAchievements();
      await showLeaderboard(submitted.displayName, submitted.xpAwarded, submitted.newBest);
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'Could not submit your run.';
    }
  }

  function renderGuestForm(result: StackTowerRunResult, submitArea: HTMLDivElement) {
    submitArea.innerHTML = `
      <form class="choice-card" data-role="name-form">
        <strong>Enter a name for the leaderboard</strong>
        <input class="join-input" data-role="name-input" maxlength="24" placeholder="Your name" aria-label="Display name" autocomplete="off" required />
        <button class="button button--primary" type="submit">Submit to leaderboard</button>
        <p class="form-error" data-role="status" aria-live="polite"></p>
      </form>
    `;
    const form = submitArea.querySelector<HTMLFormElement>('[data-role="name-form"]')!;
    const input = form.querySelector<HTMLInputElement>('[data-role="name-input"]')!;
    const status = form.querySelector<HTMLParagraphElement>('[data-role="status"]')!;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
      submitButton.disabled = true;
      status.textContent = '';
      void submitStackTowerRun(payloadFor(result, input.value))
        .then((submitted) => showLeaderboard(submitted.displayName, submitted.xpAwarded, submitted.newBest))
        .catch((error) => {
          status.textContent = error instanceof Error ? error.message : 'Could not submit your run.';
          submitButton.disabled = false;
        });
    });
  }

  async function showLeaderboard(myDisplayName: string | null, xpAwarded = 0, newBest = false) {
    stage.innerHTML = `<div class="handoff"><p class="handoff__label">Loading leaderboard…</p></div>`;
    let rows: StackTowerLeaderboardRow[];
    let myStats: StackTowerStats | null = null;
    try {
      // The personal-stats fetch 401s for a guest; catch → null so it never
      // sinks the board render, which is the part that must always work.
      const [board, stats] = await Promise.all([
        fetchStackTowerLeaderboard(mode),
        fetchStackTowerStats().then((r) => r.stats).catch(() => null),
      ]);
      rows = board.leaderboard;
      myStats = stats;
    } catch (error) {
      stage.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Error</p>
          <h2>${escapeHtml(error instanceof Error ? error.message : 'Could not load the leaderboard.')}</h2>
          <div class="hero-actions">
            <button class="button button--primary" type="button" data-action="restart">Play again</button>
            <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
          </div>
        </div>
      `;
      stage.querySelector<HTMLButtonElement>('[data-action="restart"]')?.addEventListener('click', start);
      stage.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => navigate('play'));
      return;
    }

    const mePanel =
      myStats && myStats.totalRuns > 0
        ? `<p class="settings-hint stack-tower-me">
            Your best <strong>${myStats.bestScore}</strong> ·
            ${myStats.totalRuns} run${myStats.totalRuns === 1 ? '' : 's'} ·
            ${myStats.totalPerfects} perfect${myStats.totalPerfects === 1 ? '' : 's'} ·
            best streak ${myStats.bestPerfectStreak} ·
            highest ${escapeHtml(zoneForHeight(myStats.bestScore).label)}
          </p>`
        : ''; // approximation: best score as a height proxy — stack_tower_runs has no separately tracked "height reached" for a best run to look up instead

    stage.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Top climbers</p>
        ${xpAwarded > 0 ? `<p class="stack-tower-xp">+${xpAwarded} XP${newBest ? ' · new best!' : ''}</p>` : ''}
        ${mePanel}
        <ol class="attempt-list">
          ${
            rows.length === 0
              ? `<li class="attempt-list__empty">Nobody has climbed yet. Be the first.</li>`
              : rows
                  .map(
                    (row) =>
                      `<li class="${myDisplayName && row.displayName === myDisplayName ? 'is-false-start' : ''}">
                        <span>#${row.rank}</span>
                        <span>${escapeHtml(row.displayName)}</span>
                        <strong>${row.score}</strong>
                      </li>`,
                  )
                  .join('')
          }
        </ol>
        <div class="hero-actions">
          <button class="button button--primary" type="button" data-action="restart">Play again</button>
          <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
        </div>
      </div>
    `;
    stage.querySelector<HTMLButtonElement>('[data-action="restart"]')?.addEventListener('click', start);
    stage.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => navigate('play'));
  }

    start();

    return () => {
      window.clearInterval(timerIntervalId);
      window.removeEventListener('keydown', onKeyDown);
      engine?.destroy();
    };
  };
}

export const renderStackTower = createStackTowerView('endless');
export const renderStackTower60 = createStackTowerView('60s');
